/**
 * @file AuthorSheet.tsx
 * @description Карточка автора блога (phase19.16 Р1; SB58, SB80;
 * uiux-design-r1 §7 «Карточка автора»): фото из медиатеки (без фото —
 * картинки нет, без заглушки), имя, «о себе», Telegram и ВКонтакте
 * (Instagram не показываем, SB58), адрес страницы автора, удаление через
 * окно кабинета. Правка сохраняется сразу кнопкой «Сохранить» — это
 * контент, а не вид сайта. Панель справа 480px, на телефоне — весь экран.
 */

import { useEffect, useState } from "react";

import { Button } from "../ui/button";
import { Sheet, SheetContent, SheetHeader, SheetTitle } from "../ui/sheet";
import { MediaField } from "../site-view/media/MediaField";
import { TextField } from "../site-view/TextField";
import { contentMediaUrl } from "../../lib/api-content-media";
import type { ContentAuthor } from "../../lib/api-content";
import { authorErrors, type AuthorForm } from "../../lib/content/author-form";
import type { SiteMedia } from "../../lib/site-view/types";

function photoOf(id: string | null): SiteMedia | null {
  return id ? { id, url: contentMediaUrl(id), mimeType: "image/*", width: null, height: null, altText: null } : null;
}

interface Props {
  /** `null` — закрыто, `"new"` — новый автор. */
  author: ContentAuthor | "new" | null;
  busy: boolean;
  onClose: () => void;
  onSave: (form: AuthorForm) => void;
  onDelete: (author: ContentAuthor) => void;
}

export function AuthorSheet({ author, busy, onClose, onSave, onDelete }: Props) {
  const [form, setForm] = useState<AuthorForm>({ name: "", bio: "", photo: null, telegram: "", vk: "" });
  const [touched, setTouched] = useState(false);
  const existing = author && author !== "new" ? author : null;

  useEffect(() => {
    setTouched(false);
    setForm({
      name: existing?.name ?? "",
      bio: existing?.bio ?? "",
      photo: photoOf(existing?.photoMediaId ?? null),
      telegram: existing?.telegramHandle ?? "",
      vk: existing?.vkHandle ?? "",
    });
  }, [existing, author]);

  const errors = touched ? authorErrors(form) : {};
  const patch = (next: Partial<AuthorForm>) => setForm((prev) => ({ ...prev, ...next }));

  return (
    <Sheet open={author !== null} onOpenChange={(open) => (!open ? onClose() : null)}>
      <SheetContent side="right" className="w-full max-w-none overflow-y-auto sm:max-w-[30rem]">
        <SheetHeader>
          <SheetTitle>{existing ? `Автор: ${existing.name}` : "Новый автор"}</SheetTitle>
        </SheetHeader>
        <div className="flex flex-col gap-4">
          <MediaField
            label="Фото"
            value={form.photo}
            shape="square"
            cropAspect={1}
            hint="Квадратное, от 400px. Без фото картинки не будет."
            onChange={(photo) => patch({ photo })}
          />
          <TextField label="Имя" value={form.name} max={80} error={errors.name} onChange={(name) => patch({ name })} />
          <TextField label="О себе" value={form.bio} max={300} multiline error={errors.bio} onChange={(bio) => patch({ bio })} />
          <TextField label="Telegram" value={form.telegram} placeholder="@username" error={errors.telegram} onChange={(telegram) => patch({ telegram })} />
          <TextField label="ВКонтакте" value={form.vk} placeholder="vk.com/name" error={errors.vk} onChange={(vk) => patch({ vk })} />
          {existing ? <p className="type-caption text-on-surface-variant">Адрес страницы автора: /blog/author/{existing.slug}</p> : null}
        </div>
        <div className="mt-auto flex flex-col-reverse gap-2 sm:flex-row sm:justify-between">
          {existing ? (
            <Button variant="destructive" onClick={() => onDelete(existing)}>
              Удалить автора
            </Button>
          ) : (
            <span />
          )}
          <Button
            loading={busy}
            onClick={() => {
              setTouched(true);
              if (Object.keys(authorErrors(form)).length === 0) onSave(form);
            }}
          >
            {existing ? "Сохранить" : "Добавить автора"}
          </Button>
        </div>
      </SheetContent>
    </Sheet>
  );
}
